/**
 * Chat Store (Pinia) - 军师对话页共享状态.
 *
 * 设计 (与 auth store 同一套薄 store 模式):
 *   - roles 缓存: ChatHome 拉取后缓存, Conversation 页用 role_id 反查角色名/头像,
 *     避免每次进对话页都重新拉 /roles. ensureRoles 幂等, 已加载则不重复请求.
 *   - currentConversationId: 当前所在对话, 用于返回时高亮/恢复 (轻量, 跨页可用).
 *   - 不持有 messages / streaming 状态: 那些是 Conversation 页局部状态,
 *     跨页不需要, 放 store 反而要处理多对话切换时的清理.
 */
import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { Role, UUID } from '@/types/api'
import { listRoles } from '@/api/roles'

export interface ConvTitleUpdate {
  id: UUID
  title: string
}

export const useChatStore = defineStore('chat', () => {
  const roles = ref<Role[]>([])
  const rolesLoaded = ref(false)
  const currentConversationId = ref<UUID | null>(null)
  /** 对话标题更新队列: chat SSE title 事件 -> 侧栏 watch 此 ref 实时更新列表. */
  const convTitleUpdates = ref<ConvTitleUpdate[]>([])

  /** 幂等加载角色: 首次调用拉 /roles, 后续直接返回. */
  async function ensureRoles(): Promise<void> {
    if (rolesLoaded.value) return
    roles.value = await listRoles()
    rolesLoaded.value = true
  }

  /** 用 role_id 反查角色对象 (找不到返回 undefined). */
  function getRole(roleId: UUID | null | undefined): Role | undefined {
    if (!roleId) return undefined
    return roles.value.find((r) => r.id === roleId)
  }

  function setCurrentConversation(id: UUID | null): void {
    currentConversationId.value = id
  }

  /** 通知侧栏: 对话标题已变 (后端自动取名后 SSE title 事件调用). */
  function notifyConvTitleUpdate(id: UUID, title: string): void {
    convTitleUpdates.value.push({ id, title })
  }

  return {
    roles,
    rolesLoaded,
    currentConversationId,
    convTitleUpdates,
    ensureRoles,
    getRole,
    setCurrentConversation,
    notifyConvTitleUpdate,
  }
})
